// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { AnimatePresence, motion } from "framer-motion";
import { t } from "@/lib/i18n";
import { isKaraokeNow, isKaraokeTonight, smartSortVenues, getHeroSummary, buildTonightTimeline, isLiveAtHour, DISTRICT_LABELS } from "@/lib/venue-helpers";
import { sortByDistanceOpenFirst, venueDistanceKm } from "@/lib/geo";
import { useGeolocation } from "@/hooks/useGeolocation";
import VenueCard from "@/components/VenueCard";
import HeroAnswer from "@/components/HeroAnswer";
import TonightTimeline from "@/components/TonightTimeline";
import FilterBar from "@/components/FilterBar";
import ScheduleView from "@/components/ScheduleView";
import AddVenueModal from "@/components/AddVenueModal";
import type { Venue, Locale } from "@/lib/types";

const MapView = dynamic(() => import("@/components/MapView"), { ssr: false });

interface HomeClientProps {
  venues: Venue[];
  locale: Locale;
  greetingKey: string;
  subKey: string;
}

export default function HomeClient({ venues, locale, greetingKey, subKey }: HomeClientProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [openNowOnly, setOpenNowOnly] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [privateRoomsOnly, setPrivateRoomsOnly] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [sortByNearest, setSortByNearest] = useState(false);
  const [selectedVenueSlug, setSelectedVenueSlug] = useState<string | null>(null);
  const [highlightSlug, setHighlightSlug] = useState<string | null>(null);
  const [activeHour, setActiveHour] = useState<number | null>(null);
  const [showList, setShowList] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "schedule">("list");

  // Hydration guard: geolocation is client-only. The first client paint is
  // rendered identically to the server (no coords, no distance, server sort
  // order); location-aware behaviour only switches on AFTER mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Geolocation — drives "Near me", distance badges, nearest sort, user pin.
  const { coords: rawCoords, status: rawGeoStatus, request: requestLocation } = useGeolocation();
  const coords = mounted ? rawCoords : null;
  const geoStatus = mounted ? rawGeoStatus : "idle";

  // Asking for location: when it first lands, auto-enable nearest sort so the
  // tap immediately reorders the list to closest-first.
  const handleNearMe = useCallback(() => {
    requestLocation();
    setSortByNearest(true);
  }, [requestLocation]);

  // Once we have a fix, default to nearest sort (until the user turns it off).
  useEffect(() => {
    if (geoStatus === "granted") setSortByNearest((s) => s || true);
  }, [geoStatus]);

  // Districts present in the dataset, sorted by label — powers the selector.
  const districts = useMemo(() => {
    const seen = new Set<string>();
    for (const v of venues) if (v.district) seen.add(v.district);
    return Array.from(seen)
      .map((d) => ({ value: d, label: DISTRICT_LABELS[d] ?? d }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [venues]);

  const filtered = useMemo(() => {
    let result = venues;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.name_pl?.toLowerCase().includes(q) ||
          v.address.toLowerCase().includes(q) ||
          v.description_en?.toLowerCase().includes(q),
      );
    }
    if (openNowOnly) result = result.filter((v) => isKaraokeNow(v));
    if (selectedType) result = result.filter((v) => v.venue_type === selectedType);
    if (privateRoomsOnly) result = result.filter((v) => v.has_private_rooms);
    if (selectedDistrict) result = result.filter((v) => v.district === selectedDistrict);
    if (activeHour !== null) result = result.filter((v) => isLiveAtHour(v, activeHour));
    // Nearest-first when the user has shared their location and asked for it,
    // but ALWAYS surface open venues before closed ones — so "near me" still
    // leads with somewhere you can actually go, sorted by distance within each
    // group. Otherwise the karaoke-status smart sort. Gated on `mounted` so the
    // first hydrated render uses the same order the server produced.
    return mounted && sortByNearest && coords
      ? sortByDistanceOpenFirst(result, coords, isKaraokeNow)
      : smartSortVenues(result);
  }, [venues, searchQuery, openNowOnly, selectedType, privateRoomsOnly, selectedDistrict, activeHour, sortByNearest, coords, mounted]);

  // Per-venue distance lookup so cards can show "1.2 km" without recomputing.
  const distanceFor = useCallback(
    (v: Venue) => venueDistanceKm(v, coords),
    [coords],
  );

  const openCount = useMemo(
    () => venues.filter((v) => isKaraokeNow(v)).length,
    [venues],
  );

  const tonightCount = useMemo(
    () => venues.filter((v) => isKaraokeTonight(v)).length,
    [venues],
  );

  // The single-line answer: open now / tonight / when it starts.
  const heroSummary = useMemo(
    () => getHeroSummary(venues, locale),
    [venues, locale],
  );

  // Hour-by-hour "tonight" ribbon so users see when the night starts & peaks.
  const timeline = useMemo(() => buildTonightTimeline(venues), [venues]);

  // The headline pick: prefer somewhere open right now, otherwise the best
  // spot opening later tonight — so "where tonight?" is answered at any hour.
  const topPick = !searchQuery && activeHour === null
    ? filtered.find((v) => isKaraokeNow(v)) ?? filtered.find((v) => isKaraokeTonight(v))
    : undefined;
  const restVenues = topPick ? filtered.filter((v) => v.id !== topPick.id) : filtered;

  const handleMarkerClick = useCallback((slug: string | null) => {
    setSelectedVenueSlug(slug);
  }, []);

  // Lock the document to the viewport ONLY while the map page is mounted, so a
  // map pan/fling can't chain into a page scroll. Every other page (venue
  // detail, etc.) keeps normal scrolling because the class is removed on unmount.
  useEffect(() => {
    document.body.classList.add("kc-locked");
    return () => document.body.classList.remove("kc-locked");
  }, []);

  // Mobile bottom carousel: when a pin is selected (e.g. tapped on the map),
  // scroll the matching card into view so the map selection and the card stay
  // in sync — you never lose the card the pin belongs to.
  const carouselRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!selectedVenueSlug) return;
    const el = carouselRef.current?.querySelector<HTMLElement>(
      `[data-slug="${selectedVenueSlug}"]`,
    );
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedVenueSlug]);

  return (
    <div className="flex-1 flex flex-col min-h-0">

      {/* ===== DESKTOP: Split layout ===== */}
      <div className="hidden lg:flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-[420px] shrink-0 h-full flex flex-col border-r border-white/[0.06] bg-zinc-950/50">
          <div className="shrink-0 px-5 pt-5 pb-3">
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-xl font-extrabold tracking-tight">
                {t(locale, greetingKey)}
              </h1>
              <ViewToggle mode={viewMode} onChange={setViewMode} locale={locale} />
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              {t(locale, subKey)}
              {openCount > 0 ? (
                <span className="ml-1.5 text-emerald-400 font-semibold">
                  &middot; {t(locale, "venues_open_count").replace("{count}", String(openCount))}
                </span>
              ) : tonightCount > 0 && (
                <span className="ml-1.5 text-violet-300 font-semibold">
                  &middot; {t(locale, "venues_tonight_count").replace("{count}", String(tonightCount))}
                </span>
              )}
            </p>
            {!searchQuery && viewMode === "list" && (
              <div className="mb-3">
                <HeroAnswer summary={heroSummary} locale={locale} />
              </div>
            )}
            {!searchQuery && viewMode === "list" && (
              <div className="mb-3">
                <TonightTimeline data={timeline} locale={locale} activeHour={activeHour} onSelectHour={setActiveHour} />
              </div>
            )}
            {viewMode === "list" && (
              <FilterBar
                locale={locale}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                openNowOnly={openNowOnly}
                onOpenNowChange={setOpenNowOnly}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                privateRoomsOnly={privateRoomsOnly}
                onPrivateRoomsChange={setPrivateRoomsOnly}
                geoStatus={geoStatus}
                onNearMe={handleNearMe}
                sortByNearest={sortByNearest}
                onSortNearestChange={setSortByNearest}
                districts={districts}
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
              />
            )}
          </div>
          {viewMode === "list" ? (
            <div className="flex-1 overflow-y-auto px-5 pb-8 pt-1">
              {topPick && (
                <div className="mb-3">
                  <VenueCard venue={topPick} locale={locale} index={0} featured distanceKm={distanceFor(topPick)} />
                </div>
              )}
              <div className="space-y-2">
                {restVenues.map((venue, i) => (
                  <div
                    key={venue.id}
                    onMouseEnter={() => setHighlightSlug(venue.slug)}
                    onMouseLeave={() => setHighlightSlug((s) => (s === venue.slug ? null : s))}
                  >
                    <VenueCard venue={venue} locale={locale} index={topPick ? i + 1 : i} distanceKm={distanceFor(venue)} />
                  </div>
                ))}
              </div>
              {filtered.length === 0 && <EmptyState openNowOnly={openNowOnly} onClear={() => setOpenNowOnly(false)} locale={locale} />}
            </div>
          ) : (
            <div className="flex-1 overflow-hidden px-4">
              <ScheduleView venues={venues} locale={locale} />
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 h-full">
          <MapView venues={filtered} locale={locale} height="100%" selectedSlug={selectedVenueSlug} highlightSlug={highlightSlug} onMarkerClick={handleMarkerClick} userLocation={coords} fullScreen />
        </div>
      </div>

      {/* ===== MOBILE: Map-first with bottom carousel ===== */}
      <div className="lg:hidden flex-1 flex flex-col min-h-0 relative">
        {/* Filter pills overlay on top of map */}
        <div className="absolute top-0 left-0 right-0 z-10 px-3 pb-1"
             style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
            <button
              onClick={handleNearMe}
              data-umami-event="filter-near-me"
              disabled={geoStatus === "prompting"}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                         select-none touch-manipulation backdrop-blur-md flex items-center gap-1 ${geoStatus === "granted"
                ? "bg-sky-500/20 text-sky-300 border border-sky-500/30 shadow-lg shadow-sky-900/20"
                : "bg-zinc-900/70 text-zinc-300 border border-white/[0.08] shadow-lg shadow-black/30"
              } ${geoStatus === "prompting" ? "opacity-60" : ""}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {geoStatus === "prompting"
                ? t(locale, "locating")
                : geoStatus === "insecure"
                  ? t(locale, "location_insecure")
                  : geoStatus === "denied" || geoStatus === "error" || geoStatus === "unavailable"
                    ? t(locale, "location_denied")
                    : t(locale, "near_me")}
            </button>
            <button
              onClick={() => setOpenNowOnly(!openNowOnly)}
              data-umami-event="filter-open-now"
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                         select-none touch-manipulation backdrop-blur-md ${openNowOnly
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-lg shadow-emerald-900/20"
                : "bg-zinc-900/70 text-zinc-300 border border-white/[0.08] shadow-lg shadow-black/30"
              }`}
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${
                openNowOnly ? "bg-emerald-400" : "bg-zinc-500"
              }`} />
              {t(locale, "open_venues")}
            </button>
            <button
              onClick={() => setPrivateRoomsOnly(!privateRoomsOnly)}
              data-umami-event="filter-private-rooms"
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                         select-none touch-manipulation backdrop-blur-md ${privateRoomsOnly
                ? "bg-violet-500/20 text-violet-300 border border-violet-500/30 shadow-lg shadow-violet-900/20"
                : "bg-zinc-900/70 text-zinc-300 border border-white/[0.08] shadow-lg shadow-black/30"
              }`}
            >
              {t(locale, "filter_private_rooms")}
            </button>
          </div>
        </div>

        {/* Full-bleed map */}
        <div className="flex-1 min-h-0">
          <MapView venues={filtered} locale={locale} height="100%" selectedSlug={selectedVenueSlug} highlightSlug={highlightSlug} onMarkerClick={handleMarkerClick} userLocation={coords} fullScreen />
        </div>

        {/* Bottom venue carousel */}
        <div className="absolute bottom-0 left-0 right-0 z-10 pt-8"
             style={{
               paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
               background: "linear-gradient(to top, rgba(9,9,11,0.98) 40%, rgba(9,9,11,0.7) 70%, transparent)",
             }}>
          {/* Venue count */}
          <div className="flex items-center justify-between px-4 mb-2.5">
            <span className="text-xs font-bold text-zinc-400">
              {filtered.length} {t(locale, "venues")}
              {openCount > 0 ? (
                <span className="ml-1.5 text-emerald-400">
                  &middot; {openCount} {t(locale, "open_now_short")}
                </span>
              ) : tonightCount > 0 && (
                <span className="ml-1.5 text-violet-300">
                  &middot; {tonightCount} {t(locale, "tonight_short")}
                </span>
              )}
            </span>
            <button
              onClick={() => setShowList(true)}
              data-umami-event="open-list-view"
              className="flex items-center gap-1.5 text-xs font-semibold text-violet-400 px-3 py-1.5 rounded-full
                         bg-violet-500/10 border border-violet-500/20 active:scale-95 transition-transform touch-manipulation"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              {t(locale, "list_view")}
            </button>
          </div>
          <div ref={carouselRef} className="flex gap-2.5 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-3">
            {filtered.map((venue, i) => (
              <div
                key={venue.id}
                data-slug={venue.slug}
                className={`snap-center shrink-0 w-[280px] cursor-pointer transition-transform duration-150 ${
                  selectedVenueSlug === venue.slug ? "scale-[1.02]" : ""
                }`}
                onClick={(e) => { e.preventDefault(); setSelectedVenueSlug((s) => (s === venue.slug ? null : venue.slug)); }}
              >
                <VenueCard venue={venue} locale={locale} index={i} distanceKm={distanceFor(venue)} compact noLink />
              </div>
            ))}
          </div>
        </div>

        {/* Fullscreen list overlay */}
        <AnimatePresence>
          {showList && (
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed inset-0 z-50 bg-zinc-950 flex flex-col"
            >
              <div className="shrink-0 px-4 pt-4 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-lg font-extrabold tracking-tight">
                    {t(locale, greetingKey)}
                  </h1>
                  <div className="flex items-center gap-2">
                    <ViewToggle mode={viewMode} onChange={setViewMode} locale={locale} />
                    <button
                      onClick={() => setShowList(false)}
                      data-umami-event="open-map-view"
                      className="flex items-center gap-1.5 text-sm font-medium text-violet-400 px-3 py-1.5 rounded-lg
                                 bg-violet-500/10 active:scale-95 transition-transform touch-manipulation"
                    >
                      {t(locale, "map_view")}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mb-2.5">
                  {t(locale, subKey)}
                  {openCount > 0 ? (
                    <span className="ml-1.5 text-emerald-400 font-semibold">
                      &middot; {t(locale, "venues_open_count").replace("{count}", String(openCount))}
                    </span>
                  ) : tonightCount > 0 && (
                    <span className="ml-1.5 text-violet-300 font-semibold">
                      &middot; {t(locale, "venues_tonight_count").replace("{count}", String(tonightCount))}
                    </span>
                  )}
                </p>
                {!searchQuery && viewMode === "list" && (
                  <div className="mb-2.5">
                    <HeroAnswer summary={heroSummary} locale={locale} compact />
                  </div>
                )}
                {!searchQuery && viewMode === "list" && (
                  <div className="mb-2.5">
                    <TonightTimeline data={timeline} locale={locale} activeHour={activeHour} onSelectHour={setActiveHour} compact />
                  </div>
                )}
                {viewMode === "list" && (
                  <FilterBar
                    locale={locale}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    openNowOnly={openNowOnly}
                    onOpenNowChange={setOpenNowOnly}
                    selectedType={selectedType}
                    onTypeChange={setSelectedType}
                    privateRoomsOnly={privateRoomsOnly}
                    onPrivateRoomsChange={setPrivateRoomsOnly}
                    geoStatus={geoStatus}
                    onNearMe={handleNearMe}
                    sortByNearest={sortByNearest}
                    onSortNearestChange={setSortByNearest}
                    districts={districts}
                    selectedDistrict={selectedDistrict}
                    onDistrictChange={setSelectedDistrict}
                  />
                )}
              </div>
              {viewMode === "list" ? (
                <div className="flex-1 overflow-y-auto px-4 pt-2 pb-8">
                  {topPick && (
                    <div className="mb-3">
                      <VenueCard venue={topPick} locale={locale} index={0} featured distanceKm={distanceFor(topPick)} />
                    </div>
                  )}
                  <div className="space-y-2.5">
                    {restVenues.map((venue, i) => (
                      <VenueCard key={venue.id} venue={venue} locale={locale} index={topPick ? i + 1 : i} distanceKm={distanceFor(venue)} />
                    ))}
                  </div>
                  {filtered.length === 0 && <EmptyState openNowOnly={openNowOnly} onClear={() => setOpenNowOnly(false)} locale={locale} />}
                  <AddVenueModal locale={locale} />
                </div>
              ) : (
                <div className="flex-1 overflow-hidden px-3">
                  <ScheduleView venues={venues} locale={locale} />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ViewToggle({ mode, onChange, locale }: { mode: "list" | "schedule"; onChange: (m: "list" | "schedule") => void; locale: Locale }) {
  return (
    <div className="flex bg-white/[0.04] rounded-lg p-0.5 border border-white/[0.06]">
      <button
        onClick={() => onChange("list")}
        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all touch-manipulation select-none ${
          mode === "list"
            ? "bg-white/[0.08] text-white shadow-sm"
            : "text-zinc-500 hover:text-zinc-300"
        }`}
      >
        {t(locale, "list_view")}
      </button>
      <button
        onClick={() => onChange("schedule")}
        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all touch-manipulation select-none ${
          mode === "schedule"
            ? "bg-white/[0.08] text-white shadow-sm"
            : "text-zinc-500 hover:text-zinc-300"
        }`}
      >
        {t(locale, "schedule_view")}
      </button>
    </div>
  );
}

function EmptyState({ openNowOnly, onClear, locale }: { openNowOnly: boolean; onClear: () => void; locale: Locale }) {
  return (
    <div className="text-center py-16">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center text-2xl">
        &#127908;
      </div>
      <p className="text-zinc-400 text-sm font-medium mb-1">
        {openNowOnly ? t(locale, "no_open_venues") : t(locale, "no_venues_found")}
      </p>
      {openNowOnly && (
        <button
          onClick={onClear}
          className="mt-3 px-5 py-2 rounded-full text-sm font-medium text-violet-400
                     bg-violet-500/10 border border-violet-500/20 active:scale-95 transition-transform"
        >
          {t(locale, "show_all")}
        </button>
      )}
    </div>
  );
}
